/**
 * SubscriptionSuccess.js
 * Payment confirmation page shown after successful Paddle checkout
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../utils/ThemeSwitcher';
import {
  CheckCircle2, Sparkles, ArrowRight, Mail, Calendar, Shield, Zap, Moon, Sun
} from 'lucide-react';
import logoSource from '../assets/dataquadio.png';
import logoSourceInverted from '../assets/dataquadio_inverted.png';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDarkMode, toggleTheme } = useTheme();
  const [countdown, setCountdown] = useState(10);
  
  const plan = searchParams.get('plan') || 'Pro';
  const billing = searchParams.get('billing') || 'monthly';
  const transactionId = searchParams.get('transaction_id');
  
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const email = userInfo?.email || searchParams.get('email') || '';

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); navigate('/projects'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  const planFeatures = {
    standard: ['1 project/month', 'All quality checks', 'Email support'],
    pro: ['3 projects/month', 'AI-powered analysis', 'Back-check validation', 'Priority support'],
    max: ['Unlimited projects', 'Full API access', 'Custom integrations', 'Dedicated support']
  };

  const features = planFeatures[plan.toLowerCase()] || planFeatures.pro;

  const c = {
    bg: isDarkMode ? '#08090B' : '#FFFFFF',
    bgCard: isDarkMode ? 'rgba(255,255,255,0.03)' : '#FAFAFA',
    border: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
    text: isDarkMode ? '#7d7d81' : '#111214',
    textSecondary: isDarkMode ? '#6d6e74' : '#6B7280',
    textMuted: isDarkMode ? '#5A5C66' : '#9CA3AF',
    accent: '#2B8AFF',
    accentGradient: 'linear-gradient(135deg, #2B8AFF 0%, #D4A44C 100%)',
  };

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: c.bg, color: c.text,
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Nav */}
      <nav style={{
        padding: '0 24px',
        background: isDarkMode ? 'rgba(8,9,11,0.92)' : 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${c.border}`,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img src={isDarkMode ? logoSourceInverted : logoSource} alt="DataQuad" style={{ width: '28px', height: '28px' }} />
            <span style={{ fontSize: '17px', fontWeight: '600', letterSpacing: '-0.3px' }}>dataQuad</span>
          </div>
          <button onClick={toggleTheme} style={{ background: 'none', border: 'none', color: c.textMuted, cursor: 'pointer', padding: '6px', display: 'flex' }}>
            {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '520px', margin: '0 auto', padding: '60px 24px 80px', textAlign: 'center' }}>

        {/* Success icon */}
        <div style={{
          width: '80px', height: '80px', borderRadius: '50%',
          background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px', color: '#10B981',
        }}>
          <CheckCircle2 size={40} />
        </div>

        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
          Welcome to DataQuad {plan}!
        </h1>
        <p style={{ fontSize: '16px', color: c.textSecondary, marginBottom: '32px' }}>
          Your subscription is now active
        </p>

        {/* Details card */}
        <div style={{
          borderRadius: '16px', padding: '24px',
          background: c.bgCard, border: `1px solid ${c.border}`,
          textAlign: 'left', marginBottom: '24px',
        }}>
          {[
            { label: 'Plan', value: `DataQuad ${plan}`, icon: <Sparkles size={14} style={{ color: '#F59E0B' }} /> },
            { label: 'Billing', value: billing === 'annual' ? 'Annual' : 'Monthly' },
            { label: 'Trial', value: '15 days free', icon: <Calendar size={14} style={{ color: c.accent }} /> },
            { label: 'Account', value: email },
          ].map((row, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 0',
              borderBottom: i < 3 ? `1px solid ${c.border}` : 'none',
            }}>
              <span style={{ fontSize: '14px', color: c.textMuted }}>{row.label}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {row.icon}{row.value}
              </span>
            </div>
          ))}
        </div>

        {/* Features */}
        <div style={{
          borderRadius: '16px', padding: '24px',
          background: isDarkMode ? 'rgba(43,138,255,0.06)' : 'rgba(43,138,255,0.04)',
          border: `1px solid ${isDarkMode ? 'rgba(43,138,255,0.12)' : 'rgba(43,138,255,0.08)'}`,
          textAlign: 'left', marginBottom: '24px',
        }}>
          <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Zap size={16} style={{ color: c.accent }} /> What's included:
          </div>
          {features.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: c.textSecondary, padding: '6px 0' }}>
              <CheckCircle2 size={14} style={{ color: '#10B981', flexShrink: 0 }} /> {f}
            </div>
          ))}
        </div>

        {/* Email notice */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '14px 16px', borderRadius: '12px',
          background: c.bgCard, border: `1px solid ${c.border}`,
          marginBottom: '28px', textAlign: 'left',
        }}>
          <Mail size={16} style={{ color: c.textMuted, flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: '13px', color: c.textSecondary }}>
            A confirmation email has been sent to <strong style={{ color: c.text }}>{email}</strong>
          </p>
        </div>

        {/* Actions */}
        <button onClick={() => navigate('/projects')} style={{
          width: '100%', padding: '14px', borderRadius: '12px',
          background: c.accentGradient, color: '#FFF', border: 'none',
          fontSize: '16px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          marginBottom: '12px', transition: 'all 0.2s',
        }}>
          Go to DataQuad App <ArrowRight size={18} />
        </button>

        <button onClick={() => navigate('/')} style={{
          width: '100%', padding: '12px', borderRadius: '12px',
          background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          color: c.textSecondary, border: `1px solid ${c.border}`,
          fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit',
          marginBottom: '24px', transition: 'all 0.2s',
        }}>
          Go to Home Page
        </button>

        <p style={{ fontSize: '13px', color: c.textMuted, marginBottom: '16px' }}>
          Redirecting to app in {countdown} seconds...
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', paddingTop: '16px', borderTop: `1px solid ${c.border}` }}>
          <Shield size={13} style={{ color: c.textMuted }} />
          <span style={{ fontSize: '12px', color: c.textMuted }}>Secure payment processed by Paddle</span>
        </div>

        {transactionId && (
          <p style={{ marginTop: '8px', fontSize: '11px', color: c.textMuted }}>
            Transaction ID: {transactionId}
          </p>
        )}
      </div>

      <style>{`
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}; }
      `}</style>
    </div>
  );
};

export default SubscriptionSuccess;